import { useMemo } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import type { Configuration, RedirectRequest } from "@azure/msal-browser";
import { env } from "../config/env";

export const msalConfig: Configuration = {
  auth: {
    clientId: env.AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${env.AZURE_TENANT}`,
    redirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (_level, message, containsPii) => {
        if (containsPii) return;
        if (env.isDevelopment) {
          console.log(`[MSAL] ${message}`);
        }
      },
      logLevel: env.isDevelopment ? 1 : 3, // Verbose in dev, Error in prod
    },
  },
};

export const loginRequest: RedirectRequest = {
  scopes: ["User.Read"],
  prompt: "select_account",
};

export function useMsalAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0];
  const isAuthenticated = useIsAuthenticated();

  return useMemo(
    () => ({
      account,
      instance,
      inProgress,
      isAuthenticated,

      login: async () => {
        try {
          await instance.loginRedirect(loginRequest);
        } catch (error) {
          console.error("Login failed:", error);
          throw error;
        }
      },
    
      logout: () => {
        instance.logoutRedirect();
      },
      
      getAccessToken: async () => {
        if (!account) {
          throw new Error("No account available");
        }

        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          return response.accessToken;
        } catch (error) {
          console.error("Silent token acquisition failed:", error);
          // For popup flow, we need to use popup for interactive authentication
          const response = await instance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
          return response.accessToken;
        }
      },
    }),
    [account, inProgress, instance, isAuthenticated],
  );
}
